import React, { useState } from 'react'
import {
  Alert, Box, Button, FormControl, FormControlLabel, InputLabel, MenuItem, Select, Stack, Switch,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material'
import AxisPickerModal from '../AxisPickerModal'
import FilterEditorModal from '../../spec-editor/FilterEditorModal'
import WeightPickerDialog from './WeightPickerDialog'
import CaseFilterPickerDialog from './CaseFilterPickerDialog'
import type { BatchDecimals } from '../../../hooks/useBatchRegenerate'
import {
  BatchRegenerateState, blueBoxSx,
  describeTop, describeFilter, describeWeight, describeCaseFilter, describeCells, describeDecimals,
} from './regenerateShared'

const SAMPLE_COUNT = 5

type Picker = 'top' | 'filter' | 'weight' | 'caseFilter'

interface RowProps {
  label: string
  hint: string
  mode: string
  options: { value: string; label: string }[]
  onModeChange: (mode: string) => void
  result: string
  error?: boolean
  /** Opens the row's picker again ("Choose" / "Change"). */
  onChoose?: () => void
  hasValue?: boolean
  children?: React.ReactNode
}

/** One "What would you like to change?" row: label, choice, result box. */
const ChangeRow: React.FC<RowProps> = ({ label, hint, mode, options, onModeChange, result, error, onChoose, hasValue, children }) => {
  const id = `regenerate-${label.replace(/\W+/g, '-').toLowerCase()}`
  return (
    <Box
      data-testid={`regenerate-row-${label}`}
      sx={{
        ...blueBoxSx,
        p: 1.5,
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '150px 200px 1fr' },
        gap: 2,
        alignItems: 'center',
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 600 }}>{label}</Typography>
      <Tooltip title={hint} placement="top" arrow>
        <FormControl size="small" fullWidth sx={{ backgroundColor: 'background.paper' }}>
          <InputLabel id={`${id}-label`}>{label}</InputLabel>
          <Select labelId={`${id}-label`} value={mode} label={label} onChange={e => onModeChange(e.target.value)}>
            {options.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </Select>
        </FormControl>
      </Tooltip>
      <Box
        sx={{
          backgroundColor: 'background.paper',
          border: 1,
          borderColor: error ? 'error.main' : 'divider',
          borderRadius: 1,
          px: 1.5,
          py: 1,
          minHeight: 40,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: 1,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
          <Typography
            data-testid={`regenerate-result-${label}`}
            variant="body2"
            color={error ? 'error' : mode === 'keep' ? 'text.secondary' : 'text.primary'}
            sx={{ wordBreak: 'break-word' }}
          >
            {result}
          </Typography>
          {onChoose && (
            <Button size="small" color="info" onClick={onChoose} sx={{ flexShrink: 0 }}>
              {hasValue ? 'Change' : 'Choose'}
            </Button>
          )}
        </Stack>
        {children}
      </Box>
    </Box>
  )
}

// Top has no Remove all: Carbon refuses a spec without a top (#1809).
const TOP_OPTIONS = [
  { value: 'keep', label: 'Keep original' },
  { value: 'replace', label: 'Replace with' },
]
const KEEP_REPLACE_REMOVE = [
  { value: 'keep', label: 'Keep original' },
  { value: 'replace', label: 'Replace with' },
  { value: 'remove', label: 'Remove all' },
]
const FILTER_OPTIONS = [
  { value: 'keep', label: 'Keep original' },
  { value: 'replace', label: 'Replace with' },
  { value: 'append', label: 'Append' },
  { value: 'remove', label: 'Remove all' },
]
const KEEP_CHANGE = [
  { value: 'keep', label: 'Keep original' },
  { value: 'set', label: 'Change' },
]

export interface StepChangesProps {
  batch: BatchRegenerateState
  customer: string
  job: string
}

/** Step "What would you like to change?": six rows and a few sample tables
 *  that follow the choices. */
const StepChanges: React.FC<StepChangesProps> = ({ batch, customer, job }) => {
  const [picker, setPicker] = useState<Picker | null>(null)
  const sample = batch.sourceTables.slice(0, SAMPLE_COUNT)

  const decimalField = (label: string, key: keyof BatchDecimals) => (
    <TextField
      label={label}
      type="number"
      size="small"
      value={Number.isNaN(batch.decimals[key]) ? '' : batch.decimals[key]}
      onChange={e => batch.setDecimals({ [key]: e.target.value === '' ? NaN : Number(e.target.value) })}
      inputProps={{ min: 0, max: 9, step: 1 }}
      error={batch.decimalsInvalid}
      sx={{ width: 130 }}
    />
  )
  const cellSwitch = (label: string, key: 'frequencies' | 'columnPercents' | 'rowPercents') => (
    <FormControlLabel
      control={<Switch color="info" size="small" checked={batch.cells[key]} onChange={e => batch.setCells({ [key]: e.target.checked })} />}
      label={label}
    />
  )

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>What would you like to change?</Typography>
      <Stack spacing={1.5}>
        <ChangeRow
          label="Top"
          hint="Replace the top spec of every table. A table always needs a top."
          mode={batch.topMode}
          options={TOP_OPTIONS}
          onModeChange={mode => {
            batch.setTopMode(mode as typeof batch.topMode)
            if (mode === 'replace') setPicker('top')
          }}
          result={describeTop(batch.topMode, batch.topValue)}
          error={batch.topMode === 'replace' && !batch.topValue.trim()}
          onChoose={batch.topMode === 'replace' ? () => setPicker('top') : undefined}
          hasValue={!!batch.topValue}
        />
        <ChangeRow
          label="Filter"
          hint="Replace, add to or remove the filter of every table"
          mode={batch.filterMode}
          options={FILTER_OPTIONS}
          onModeChange={mode => {
            batch.setFilterMode(mode as typeof batch.filterMode)
            if (mode === 'replace' || mode === 'append') setPicker('filter')
          }}
          result={describeFilter(batch.filterMode, batch.filterValue)}
          error={(batch.filterMode === 'replace' || batch.filterMode === 'append') && !batch.filterValue.trim()}
          onChoose={batch.filterMode === 'replace' || batch.filterMode === 'append' ? () => setPicker('filter') : undefined}
          hasValue={!!batch.filterValue}
        />
        <ChangeRow
          label="Weight"
          hint="Replace or remove the weight of every table"
          mode={batch.weightMode}
          options={KEEP_REPLACE_REMOVE}
          onModeChange={mode => {
            batch.setWeightMode(mode as typeof batch.weightMode)
            if (mode === 'replace') setPicker('weight')
          }}
          result={describeWeight(batch.weightMode, batch.weightValue)}
          error={batch.weightMode === 'replace' && !batch.weightValue.trim()}
          onChoose={batch.weightMode === 'replace' ? () => setPicker('weight') : undefined}
          hasValue={!!batch.weightValue}
        />
        <ChangeRow
          label="Case filter"
          hint="Replace or remove the case filter of every table"
          mode={batch.caseFilterMode}
          options={KEEP_REPLACE_REMOVE}
          onModeChange={mode => {
            batch.setCaseFilterMode(mode as typeof batch.caseFilterMode)
            if (mode === 'replace') setPicker('caseFilter')
          }}
          result={describeCaseFilter(batch.caseFilterMode, batch.caseFilterValue)}
          error={batch.caseFilterMode === 'replace' && !batch.caseFilterValue.trim()}
          onChoose={batch.caseFilterMode === 'replace' ? () => setPicker('caseFilter') : undefined}
          hasValue={!!batch.caseFilterValue}
        >
          {batch.caseFilterChecking && (
            <Typography variant="caption" color="text.secondary">Checking the case filter on the first table…</Typography>
          )}
          {batch.caseFilterEmpty && (
            <Alert severity="warning" data-testid="regenerate-case-filter-empty">
              Please try another variable. Case filter only accepts <strong>single response</strong> variables.
              Case filters do not accept multi-response variables (where you can select more than one answer) or
              hierarchical variables.
            </Alert>
          )}
        </ChangeRow>
        <ChangeRow
          label="Cell Display"
          hint="Choose which values every table shows: frequencies and percents"
          mode={batch.cellsMode}
          options={KEEP_CHANGE}
          onModeChange={mode => batch.setCellsMode(mode as typeof batch.cellsMode)}
          result={describeCells(batch.cellsMode, batch.cells)}
          error={batch.cellsInvalid}
        >
          {batch.cellsMode === 'set' && (
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {cellSwitch('Frequencies', 'frequencies')}
              {cellSwitch('Column %', 'columnPercents')}
              {cellSwitch('Row %', 'rowPercents')}
            </Stack>
          )}
        </ChangeRow>
        <ChangeRow
          label="Decimal places"
          hint="Set the decimal places of every table"
          mode={batch.decimalsMode}
          options={KEEP_CHANGE}
          onModeChange={mode => batch.setDecimalsMode(mode as typeof batch.decimalsMode)}
          result={describeDecimals(batch.decimalsMode, batch.decimals, batch.decimalsInvalid)}
          error={batch.decimalsInvalid}
        >
          {batch.decimalsMode === 'set' && (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Number of decimal places
              </Typography>
              <Stack direction="row" spacing={2}>
                {decimalField('Frequencies', 'frequencies')}
                {decimalField('Percents', 'percents')}
              </Stack>
            </Box>
          )}
        </ChangeRow>
      </Stack>

      <Typography variant="subtitle2" sx={{ mt: 3, mb: 1, color: 'text.secondary' }}>
        Sample tables{batch.sourceTables.length > sample.length ? ` (${sample.length} of ${batch.sourceTables.length.toLocaleString()})` : ''}
      </Typography>
      <TableContainer data-testid="regenerate-sample" sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {['Table', 'Top', 'Filter', 'Weight', 'Case filter'].map(h => (
                <TableCell key={h} sx={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary' }}>
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sample.map(t => (
              <TableRow key={t.path}>
                <TableCell sx={{ fontWeight: 600 }}>{batch.newTableName(t.name)}</TableCell>
                {[
                  batch.resolveTop(t.spec.top || ''),
                  batch.resolveFilter(t.spec.filter || ''),
                  batch.resolveWeight(t.spec.weight || ''),
                  batch.resolveCaseFilter(t.spec.caseFilter || ''),
                ].map((value, i) => (
                  <TableCell
                    key={i}
                    title={value}
                    sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'text.secondary', fontSize: '0.8rem' }}
                  >
                    {value || '—'}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <AxisPickerModal
        open={picker === 'top'}
        onClose={() => setPicker(null)}
        onSave={syntax => batch.setTopValue(syntax)}
        customer={customer}
        job={job}
        initialSyntax={batch.topValue}
        title="Top Axis"
      />
      <FilterEditorModal
        open={picker === 'filter'}
        onClose={() => setPicker(null)}
        currentFilter={batch.filterValue}
        onSave={filter => {
          batch.setFilterValue(filter)
          setPicker(null)
        }}
        customer={customer}
        job={job}
      />
      <WeightPickerDialog
        open={picker === 'weight'}
        onClose={() => setPicker(null)}
        onSelect={weight => batch.setWeightValue(weight)}
        customer={customer}
        job={job}
        inUse={[...new Set(batch.sourceTables.map(t => (t.spec.weight || '').trim()).filter(Boolean))]}
      />
      <CaseFilterPickerDialog
        open={picker === 'caseFilter'}
        onClose={() => setPicker(null)}
        onSave={syntax => batch.setCaseFilterValue(syntax)}
        customer={customer}
        job={job}
      />
    </Box>
  )
}

export default StepChanges
